import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ============================
   FETCH ALL CATEGORIES
============================ */
export const fetchCategories = createAsyncThunk(
  "category/fetchCategories",
  async ({ page = 1, limit = 10, search = "" }, thunkAPI) => { // ✅ add search
    try {
      const res = await api.get(
        `/api/categories?page=${page}&limit=${limit}&search=${search}` // ✅ add &search
      );
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch categories");
    }
  }
);
/* ============================
   FETCH DROPDOWN
============================ */
export const fetchCategoryDropdown = createAsyncThunk(
  "category/fetchDropdown",
  async (_, thunkAPI) => {
    try {
      const res = await api.get(`/api/categories/dropdown`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch dropdown");
    }
  }
);

/* ============================
   CREATE CATEGORY
============================ */
export const createCategory = createAsyncThunk(
  "category/createCategory",
  async (formData, thunkAPI) => {
    try {
      const res = await api.post(`/api/categories`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to create category"
      );
    }
  }
);

/* ============================
   UPDATE CATEGORY
============================ */
export const updateCategory = createAsyncThunk(
  "category/updateCategory",
  async ({ id, formData }, thunkAPI) => {
    try {
      const res = await api.put(`/api/categories/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to update category"
      );
    }
  }
);

/* ============================
   TOGGLE STATUS
============================ */
export const toggleCategoryStatus = createAsyncThunk(
  "category/toggleStatus",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/api/categories/${id}/status`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update status");
    }
  }
);

/* ============================
   DELETE CATEGORY
============================ */
export const deleteCategory = createAsyncThunk(
  "category/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/api/categories/${id}`);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Delete failed"
      );
    }
  }
);

/* ============================
   SLICE
============================ */
const categorySlice = createSlice({
  name: "category",
  initialState: {
    categories: [],
    dropdownCategories: [],
    loading: false,
    error: null,
    pagination: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Dropdown
      .addCase(fetchCategoryDropdown.fulfilled, (state, action) => {
        state.dropdownCategories = action.payload;
      })

      // Create
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categories.unshift(action.payload);
        state.pagination.total = (state.pagination.total || 0) + 1;
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Update
      .addCase(updateCategory.fulfilled, (state, action) => {
        const index = state.categories.findIndex(
          (c) => c._id === action.payload._id
        );
        if (index !== -1) state.categories[index] = action.payload;
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Toggle Status
      .addCase(toggleCategoryStatus.fulfilled, (state, action) => {
        const index = state.categories.findIndex(
          (c) => c._id === action.payload._id
        );
        if (index !== -1) state.categories[index] = action.payload;
      })

      // Delete
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(
          (c) => c._id !== action.payload
        );
        state.pagination.total = (state.pagination.total || 1) - 1;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default categorySlice.reducer;